import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { type ReactNode, useState } from 'react'

import {
  Button,
  Field,
  HelpHint,
  Input,
  Page,
  PageHeader,
  RadioGroup,
  Section,
  Select,
  Spinner,
  SwitchField,
} from '@aeris/ui'

import {
  ProductMultiPicker,
  type SelectedProduct,
} from '../components/product-multi-picker.tsx'
import {
  ApiError,
  createFollowupRule,
  fetchChannelAccounts,
  fetchFollowupRules,
  fetchProducts,
  fetchTemplates,
  updateFollowupRule,
} from '../lib/api.ts'

/**
 * CRM → Regras → Nova/Editar (F065): a regra vira PÁGINA e aceita vários
 * produtos.
 *
 * **Vários produtos** era o pedido literal do dono ("aqui tenho que
 * conseguir adicionar vários produtos tbm"). "Quem comprar ar-condicionado
 * OU ventilador, lembrar da manutenção em 6 meses" era, até aqui, duas
 * regras — dois prazos para manter em sincronia, e dois lugares para
 * esquecer de mexer.
 *
 * **Página e não modal** pelo mesmo motivo das filas (F058) e das campanhas
 * (F060): a lista de produtos cresce, e um diálogo de altura fixa com uma
 * lista dentro empurra os botões para o meio da caixa.
 *
 * **Sem `useForm`.** A versão anterior usava `watch()`, que o React Compiler
 * recusa memoizar (o lint avisa em `crm-regras.tsx:210` desde que a regra
 * entrou) — o componente inteiro saía da compilação. Estado local simples
 * resolve: são nove campos, três deles condicionais, e nenhuma validação que
 * o servidor já não faça e devolva em português.
 */

type TriggerKind = 'product_purchased' | 'category_purchased'

const TRIGGER_OPTIONS = [
  {
    value: 'product_purchased',
    label: 'Produtos específicos',
    description:
      'Um ou vários — a regra dispara se qualquer um deles aparecer.',
  },
  {
    value: 'category_purchased',
    label: 'Qualquer produto de uma marca',
    description: 'O Aeris não tem tabela de categoria; casa com a marca.',
  },
] as const

export const CrmRegrasEditarPage = (): ReactNode => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // `strict: false` porque o MESMO componente serve `/crm-regras/nova` (sem
  // parâmetro) e `/crm-regras/$id` — o roteador só conhece `id` na segunda.
  const params = useParams({ strict: false })
  const ruleId = params.id

  const rules = useQuery({
    queryKey: ['communication-followup-rules'],
    queryFn: async ({ signal }) => await fetchFollowupRules(signal),
  })
  const initial =
    ruleId === undefined
      ? null
      : (rules.data ?? []).find((rule) => rule.id === ruleId)

  const accounts = useQuery({
    queryKey: ['communication-channel-accounts'],
    queryFn: async ({ signal }) => await fetchChannelAccounts(signal),
    // Modelo, campanha e follow-up são da API oficial: o aparelho vinculado
    // não tem modelo e não pode abrir conversa com quem nunca escreveu (F192).
    select: (rows) =>
      rows.filter((account) => account.connection === 'cloud_api'),
  })

  /**
   * Os nomes dos produtos que a regra JÁ tem — a lista chega como uuid, e um
   * chip com uuid não diz nada a ninguém. Uma consulta só, e apenas quando há
   * o que resolver.
   */
  const initialProducts = useQuery({
    queryKey: ['followup-rule-products', ruleId],
    queryFn: async ({ signal }) => {
      const wanted = new Set(initial?.productIds ?? [])
      if (wanted.size === 0) return [] as SelectedProduct[]
      // `pageSize` alto de propósito: a regra pode ter até 50 produtos, e
      // buscar um a um seria 50 idas ao servidor para desenhar 50 marcadores.
      const page = await fetchProducts(
        { status: 'active', pageSize: 200 },
        signal
      )
      return page.items
        .filter((item) => wanted.has(item.id))
        .map((item) => ({ id: item.id, name: item.name }))
    },
    enabled: initial !== null && initial !== undefined,
  })

  const [ready, setReady] = useState(false)
  const [accountId, setAccountId] = useState('')
  const [name, setName] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [triggerKind, setTriggerKind] =
    useState<TriggerKind>('product_purchased')
  const [products, setProducts] = useState<readonly SelectedProduct[]>([])
  const [brand, setBrand] = useState('')
  const [delayMonths, setDelayMonths] = useState(6)
  const [delayDays, setDelayDays] = useState(0)
  const [templateId, setTemplateId] = useState('')
  const [cancelOnRepurchase, setCancelOnRepurchase] = useState(true)
  const [formError, setFormError] = useState<string | null>(null)

  // Semear o formulário uma vez, quando a regra e os nomes dos produtos
  // chegarem. Um `useEffect` faria o mesmo e reagiria a cada render; um
  // `ready` explícito diz em uma linha o que está acontecendo.
  if (
    !ready &&
    initial !== null &&
    initial !== undefined &&
    !initialProducts.isPending
  ) {
    setReady(true)
    setAccountId(initial.accountId)
    setName(initial.name)
    setEnabled(initial.enabled)
    setTriggerKind(initial.triggerKind)
    setProducts(initialProducts.data ?? [])
    setBrand(initial.productBrand ?? '')
    setDelayMonths(initial.delayMonths)
    setDelayDays(initial.delayDays)
    setTemplateId(initial.templateId)
    setCancelOnRepurchase(initial.cancelOnRepurchase)
  }

  const templates = useQuery({
    queryKey: ['communication-templates', accountId],
    queryFn: async ({ signal }) =>
      await fetchTemplates(accountId === '' ? undefined : accountId, signal),
    enabled: accountId !== '',
  })
  const approvedTemplates = (templates.data ?? []).filter(
    (template) => template.status === 'approved'
  )

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        accountId,
        name,
        enabled,
        triggerKind,
        productIds:
          triggerKind === 'product_purchased'
            ? products.map((product) => product.id)
            : [],
        productBrand:
          triggerKind === 'category_purchased' && brand.trim() !== ''
            ? brand.trim()
            : null,
        delayMonths,
        delayDays,
        templateId,
        cancelOnRepurchase,
      }

      if (initial === null || initial === undefined) {
        await createFollowupRule(payload)
      } else {
        await updateFollowupRule(initial.id, {
          ...payload,
          version: initial.version,
        })
      }
    },
    onSuccess: async () => {
      setFormError(null)
      await queryClient.invalidateQueries({
        queryKey: ['communication-followup-rules'],
      })
      await navigate({ to: '/crm-regras' })
    },
    onError: (cause: unknown) => {
      setFormError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar esta regra.'
      )
    },
  })

  const submit = (): void => {
    setFormError(null)

    if (accountId === '') {
      setFormError('Escolha a conta por onde a mensagem sai.')
      return
    }
    if (name.trim() === '') {
      setFormError('Dê um nome à regra.')
      return
    }
    if (triggerKind === 'product_purchased' && products.length === 0) {
      setFormError('Escolha ao menos um produto que dispara esta regra.')
      return
    }
    if (triggerKind === 'category_purchased' && brand.trim() === '') {
      setFormError('Escolha a marca que dispara esta regra.')
      return
    }
    if (delayMonths === 0 && delayDays === 0) {
      setFormError('Informe um prazo maior que zero.')
      return
    }
    if (templateId === '') {
      setFormError('Escolha o modelo aprovado que será enviado.')
      return
    }

    save.mutate()
  }

  if (ruleId !== undefined && rules.isPending) {
    return (
      <Page>
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link
            to="/crm-regras"
            className="text-(--brand-primary) hover:underline"
          >
            ← Regras de follow-up
          </Link>
        }
        title={ruleId === undefined ? 'Nova regra' : 'Editar regra'}
        description='"Comprou isto? Fale com ele daqui a tanto tempo, com este modelo."'
      />

      <Section
        title="O que dispara"
        description="A regra vale para vendas CONCLUÍDAS — um orçamento ou uma venda cancelada não conta."
      >
        <Field label="Conta" required>
          {(field) => (
            <Select
              {...field}
              value={accountId}
              disabled={accounts.isPending}
              onChange={(event) => {
                setAccountId(event.target.value)
                setTemplateId('')
              }}
            >
              <option value="">Escolha uma conta</option>
              {(accounts.data ?? []).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.displayName}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Nome da regra" required>
          {(field) => (
            <Input
              {...field}
              value={name}
              placeholder="Manutenção de ar-condicionado"
              onChange={(event) => {
                setName(event.target.value)
              }}
            />
          )}
        </Field>

        <SwitchField
          label="Regra ligada"
          description="Desligada, ela para de materializar tarefas novas — as pendentes continuam."
          checked={enabled}
          onCheckedChange={setEnabled}
        />

        <Field label="Dispara quando o cliente comprar" required>
          {(field) => (
            <RadioGroup
              {...field}
              value={triggerKind}
              onValueChange={setTriggerKind}
              options={TRIGGER_OPTIONS}
            />
          )}
        </Field>

        {triggerKind === 'product_purchased' ? (
          <Field
            label="Produtos"
            required
            hint="A regra dispara quando QUALQUER um deles aparece numa venda concluída. Uma venda com dois produtos da lista gera um lembrete só."
          >
            {() => (
              <ProductMultiPicker
                value={products}
                queryKeyPrefix="followup-product-picker"
                onChange={setProducts}
              />
            )}
          </Field>
        ) : (
          <Field
            label="Marca"
            required
            hint='O Aeris não tem uma tabela de categoria — casa com a marca do produto (ex.: "Samsung"), sem diferenciar maiúsculas.'
          >
            {(field) => (
              <Input
                {...field}
                value={brand}
                placeholder="Samsung"
                onChange={(event) => {
                  setBrand(event.target.value)
                }}
              />
            )}
          </Field>
        )}
      </Section>

      <Section
        title="Quando falar, e o quê"
        description="Um follow-up de meses está sempre fora da janela de 24 horas — só um modelo aprovado pela Meta pode ser enviado."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Prazo — meses"
            help={({ id }) => (
              <HelpHint label="prazo" id={id}>
                Tempo entre a venda concluída e o disparo do lembrete — meses e
                dias somam. É aritmética de calendário em UTC, não um número
                fixo de dias: "6 meses" cai no mesmo dia do mês seguinte, não em
                180 dias.
              </HelpHint>
            )}
          >
            {(field) => (
              <Input
                {...field}
                type="number"
                min={0}
                max={120}
                value={delayMonths}
                onChange={(event) => {
                  setDelayMonths(Number(event.target.value))
                }}
              />
            )}
          </Field>
          <Field label="Prazo — dias">
            {(field) => (
              <Input
                {...field}
                type="number"
                min={0}
                max={3660}
                value={delayDays}
                onChange={(event) => {
                  setDelayDays(Number(event.target.value))
                }}
              />
            )}
          </Field>
        </div>

        <Field label="Modelo aprovado" required>
          {(field) => (
            <Select
              {...field}
              value={templateId}
              disabled={accountId === '' || templates.isPending}
              onChange={(event) => {
                setTemplateId(event.target.value)
              }}
            >
              <option value="">
                {accountId === ''
                  ? 'Escolha a conta primeiro'
                  : 'Escolha um modelo'}
              </option>
              {approvedTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.metaTemplateName}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {accountId !== '' &&
        !templates.isPending &&
        approvedTemplates.length === 0 ? (
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Nenhum modelo aprovado nesta conta ainda.{' '}
            <Link
              to="/atendimento-modelos"
              className="text-(--brand-primary) hover:underline"
            >
              Criar um modelo
            </Link>{' '}
            — a análise da Meta leva alguns dias.
          </p>
        ) : null}

        <SwitchField
          label="Cancelar o lembrete se o cliente comprar de novo antes do vencimento"
          description="Evita mandar “hora da manutenção” para quem acabou de trocar de peça. Desligado, o lembrete original dispara mesmo assim."
          checked={cancelOnRepurchase}
          onCheckedChange={setCancelOnRepurchase}
        />
      </Section>

      {formError === null ? null : (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {formError}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button asChild variant="ghost">
          <Link to="/crm-regras">Cancelar</Link>
        </Button>
        <Button
          type="button"
          variant="primary"
          loading={save.isPending}
          onClick={submit}
        >
          {ruleId === undefined ? 'Criar regra' : 'Salvar'}
        </Button>
      </div>
    </Page>
  )
}
